import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Icon,
  Box,
  Text,
  Badge,
  SkeletonCircle,
  Skeleton,
  useDisclosure,
} from '@chakra-ui/react';
import { differenceInSeconds } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { IconBell, IconCheckCircleWhite, IconTimesCircle, IconView } from 'assets/icons';
import { routePaths } from 'global/routePaths';
import { useFetchPaginatedNotification } from 'hooks/notification/useFetchPaginatedNotification';
import { useMarkAllRead } from 'hooks/notification/useMarkAllRead';
import { secondsToHm } from 'utils/helpers';
import toaster from 'utils/toaster';

const searchQuery = {
  page: 1,
  page_size: 35,
};

const NotificationMenu: React.FC<unknown> = () => {
  const { t } = useTranslation(['common']);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [enabledMarkAllRead, setEnableMarkAllRead] = useState<boolean>(false);

  const { status, data, refetch } = useFetchPaginatedNotification(searchQuery, { enabled: isOpen });

  useMarkAllRead({
    enabled: enabledMarkAllRead,
    onSettled: (data: any) => {
      setEnableMarkAllRead(false);
      if (data) {
        if (data?.message) toaster.success({ description: data?.message });
        refetch();
      }
    },
  });

  const renderDuration = useCallback((notification: any) => {
    const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (notification?.created_at) {
      const localCreatedTime = toZonedTime(`${notification?.created_at}Z`, localTimezone);

      const intervalDuration = differenceInSeconds(new Date(), localCreatedTime);
      const { hours, minutes } = secondsToHm(intervalDuration);
      return `${hours}h ${minutes}m`;
    }

    return '';
  }, []);

  const handleMarkAllRead = () => {
    setEnableMarkAllRead(true);
  };

  return (
    <Menu isLazy closeOnBlur autoSelect={false} isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
      <MenuButton>
        <Box
          pos="relative"
          mr={{ base: '3', md: '6' }}
          sx={
            {
              // _after: {
              //   // TODO: Replace the 1 with notification data from the API once its ready
              //   content: `"1"`,
              //   position: 'absolute',
              //   color: 'white',
              //   border: '1px solid #fff',
              //   fontSize: 'xs',
              //   borderRadius: '50%',
              //   bg: 'secondary.500',
              //   display: 'flex',
              //   alignItems: 'center',
              //   justifyContent: 'center',
              //   w: '17px',
              //   h: '17px',
              //   top: '7px',
              //   right: '7px',
              // },
            }
          }
        >
          <Icon
            as={IconBell}
            sx={{
              path: {
                fill: 'primary.500',
              },
            }}
            width={{ base: '40px', md: '48px' }}
            height={{ base: '40px', md: '48px' }}
            p={{ base: '2', md: '3.5' }}
            cursor="pointer"
          />
        </Box>
      </MenuButton>
      <MenuList overflow="hidden" py="0" minWidth="0">
        <Box maxHeight="350px" width="350px">
          {status === 'loading'
            ? Array.from(new Array(7)).map((_, index: number) => (
                <Box padding="3" display="flex" key={index} width="100%">
                  <SkeletonCircle size="8" mr="3" />
                  <Box flex="1 1 auto">
                    <Skeleton width="50%" height="10px" mb="2" />
                    <Skeleton width="100%" height="10px" />
                  </Box>
                </Box>
              ))
            : null}
          {status === 'error' ? (
            <Box display="flex" justifyContent="center" overflowY="auto" width="100%" p="3">
              <Text textAlign="center">{t('common:networkError')}.</Text>
            </Box>
          ) : null}
          {status === 'success' && Array.isArray(data?.payload) && data?.payload?.length ? (
            <>
              <Box maxHeight="315px" overflowY="auto" width="100%">
                {data?.payload?.map((notification: any) => (
                  <React.Fragment key={notification?.id}>
                    <MenuItem
                      as={RouterLink}
                      to={routePaths.notification.detail.replace(':id', notification?.id)}
                      icon={
                        notification?.type === 'success' ? (
                          <Icon as={IconCheckCircleWhite} sx={{ '& path': { fill: 'success.500' } }} boxSize="32px" />
                        ) : (
                          <Icon as={IconTimesCircle} sx={{ '& path': { fill: 'red.500' } }} boxSize="32px" />
                        )
                      }
                    >
                      <Box>
                        <Text fontWeight="bold">{notification?.entity || ''}</Text>
                        <Text fontSize="13px">{notification?.message || ''}</Text>
                        <Badge colorScheme="primary" fontSize="12px" textTransform="lowercase">
                          {renderDuration(notification)}
                        </Badge>
                      </Box>
                    </MenuItem>
                    <MenuDivider my="1" />
                  </React.Fragment>
                ))}
              </Box>
              <MenuDivider my="0" />
              <Box display="flex" justifyContent="space-between" p="0.4rem 0.8rem">
                <Text
                  as={RouterLink}
                  to={routePaths.notification.base}
                  display="flex"
                  alignItems="center"
                  cursor="pointer"
                  onClick={onClose}
                >
                  <Icon as={IconView} mr="2" />
                  {t('common:seeAll')}
                </Text>
                <Text display="flex" alignItems="center" cursor="pointer" onClick={handleMarkAllRead}>
                  <Icon as={IconTimesCircle} mr="2" />
                  {t('common:markAllRead')}
                </Text>
              </Box>
            </>
          ) : null}
          {status === 'success' && Array.isArray(data?.payload) && !data?.payload?.length ? (
            <Box display="flex" justifyContent="center" overflowY="auto" width="100%" p="3">
              <Text textAlign="center">{t('common:noNotificationFound')}</Text>
            </Box>
          ) : null}
        </Box>
      </MenuList>
    </Menu>
  );
};

export default NotificationMenu;
